

















































































































































import { Component, Vue } from 'vue-property-decorator';
import { ValidationObserver, ValidationProvider } from 'vee-validate-3';
import isEqual from 'lodash/isEqual';

import Input from '@/componentsV2/molecules/Inputs/TextField.vue';
import PhoneInput from '@/components/molecules/PhoneInput/PhoneInput.vue';
import FilledButton from '@/components/molecules/FilledButton/FilledButton.vue';
import SelectDropdownField from '@/componentsV2/molecules/SelectDropdownField/SelectDropdownField.vue';
import Typography from '@/componentsV2/atoms/Typography/Typography.vue';

import { IBusinessType } from '@/models/business';
import { IProvider } from '@/models/provider';
import { VueMultiselectOptions } from '@/models/select-dropdown';

import SignUpService from '@/api/SingupService';
import BusinessService from '@/api/BusinessService';

import { setFocusOnFirstInput } from '@/utils/setFocusOnFirstInput';
import SelectDropDownFieldUtils from '@/utils/selectDropdownFieldUtils';
import eventBus from '@/utils/eventBus';
import { removeProtocol } from '@/utils/urlUtils';
import { omitNilProperties } from '@/utils/omitNilProperties';
import {
  ESocialUrl,
  getSocialProfile,
  SOCIAL_PROFILE_NAMES
} from '@/utils/socialLinks';
import { isOtherCategory } from '@/components/organisms/Categories/Categories.vue';
import segmentIo from '@/utils//segment.io';

type Option = {
  value: string;
  label: string;
};

type FormData = {
  name: string;
  category: Option | null;
  otherCategory: string;
  website: string;
  facebookLink: string;
  instagramLink: string;
  phone: {
    code: string;
    iso: string;
    number: string;
  };
};

function defaultOptionValue() {
  return {
    value: '',
    label: ''
  };
}

function defaultFormData(): FormData {
  return {
    name: '',
    website: '',
    category: null,
    otherCategory: '',
    facebookLink: '',
    instagramLink: '',
    phone: {
      code: '',
      iso: '',
      number: ''
    }
  };
}

@Component({
  components: {
    ValidationObserver,
    ValidationProvider,
    Input,
    PhoneInput,
    FilledButton,
    SelectDropdownField,
    Typography
  }
})
export default class GeneralInfo extends Vue {
  $refs!: {
    observer: InstanceType<typeof ValidationObserver>;
  };

  ESocialUrl = ESocialUrl;

  signUpService = new SignUpService();
  businessService = new BusinessService();

  socialKey = 0;
  initialCategory = defaultOptionValue();
  categories: VueMultiselectOptions[] = [];
  categoriesSelectOptions = {
    ...SelectDropDownFieldUtils.getSelectdropdownPropertiesDefault(),
    searchable: false
  };
  loadingCategories = false;
  loading = false;
  formData: FormData = defaultFormData();
  formDataClone: FormData = defaultFormData();
  isOtherCategory = isOtherCategory;

  get businessInfo() {
    return this.$store.getters['business/getBusinessInfo'].business;
  }

  get provider() {
    return this.$store.getters['merchant/getCurrentProvider'] as IProvider;
  }

  get shouldValidateWebsite() {
    return [IBusinessType.ONLINE, IBusinessType.BOTH].includes(
      this.businessInfo.type
    );
  }

  get shouldDisableWebsiteField() {
    return this.provider && this.provider.type === 'online';
  }

  get isEqualForm() {
    return isEqual(this.formData, this.formDataClone);
  }

  mounted() {
    this.fillInitialFormData();
  }

  async loadCategories() {
    try {
      this.loadingCategories = true;

      const { data } = await this.signUpService.getCategories();

      const categories = data.categories.map(
        (category: { id: string; name: string }) => ({
          value: category.id,
          label: category.name
        })
      );

      this.categories = categories;
    } finally {
      this.loadingCategories = false;
    }
  }

  setFormDataClone(formData: FormData) {
    this.formDataClone = {
      ...formData,
      phone: Object.assign({}, formData.phone)
    };
  }

  async onSubmit() {
    const formRef = this.$refs.observer;
    const formIsValid = await formRef.validate();

    if (!formIsValid) {
      setFocusOnFirstInput('input[aria-invalid="true"]');

      return;
    }

    this.formData.instagramLink = getSocialProfile(
      SOCIAL_PROFILE_NAMES.INSTAGRAM,
      this.formData.instagramLink
    );
    this.formData.facebookLink = getSocialProfile(
      SOCIAL_PROFILE_NAMES.FACEBOOK,
      this.formData.facebookLink
    );

    this.socialKey++;

    const {
      name,
      category,
      otherCategory,
      website,
      phone,
      facebookLink,
      instagramLink
    } = this.formData;

    const labelType: Record<IBusinessType, string> = {
      [IBusinessType.BOTH]: 'Physical and online',
      [IBusinessType.INSTORE]: 'Physical',
      [IBusinessType.ONLINE]: 'Online'
    };

    const payload = {
      name,
      categoryId: category!.value,
      otherCategory: this.isOtherCategory(category!.label) ? otherCategory : '',
      type: labelType[this.businessInfo.type as IBusinessType],
      website: website ? website : null,
      phone: phone.number,
      phoneCountryCode: phone.code,
      phoneCountryIso: phone.iso,
      logoId: this.businessInfo.logo.id,
      facebookLink: this.formData.facebookLink
        ? ESocialUrl.FACEBOOK + facebookLink
        : null,
      instagramLink: this.formData.instagramLink
        ? ESocialUrl.INSTAGRAM + instagramLink
        : null
    };

    try {
      this.loading = true;

      const response = await this.businessService.updateProfile(payload);

      this.$notification('Profile updated successfully!');
      eventBus.$emit('reloadBusiness');

      if (payload.name !== this.businessInfo.name) {
        eventBus.$emit('reloadBusinessMenuDropdown');
      }

      this.$store.dispatch('business/SET_BUSINESS_INFO', {
        ...this.businessInfo,
        ...omitNilProperties(response.data),
        category: {
          id: category!.value,
          name: category!.label
        },
        website: payload.website,
        facebookLink: payload.facebookLink,
        instagramLink: payload.instagramLink
      });
      this.$store.dispatch('business/UPDATE_BUSINESS_PHONE', phone);

      segmentIo.track('Business Profile Updated', {
        Name: payload.name,
        Category: payload.categoryId,
        'Other Category': payload.otherCategory,
        Website: payload.website,
        Phone: payload.phone,
        'Facebook Link': payload.facebookLink,
        'Instagram Link': payload.instagramLink
      });

      this.setFormDataClone(this.formData);
    } finally {
      this.loading = false;
    }
  }

  async fillInitialFormData() {
    if (!this.businessInfo) return;

    const {
      name,
      phoneCountryCode,
      phoneCountryIso = '',
      countryIso2 = '',
      otherCategory = '',
      phone,
      website,
      category = {},
      facebookLink = '',
      instagramLink = ''
    } = this.businessInfo;

    const categoryData = {
      value: category.id || '',
      label: category.name || ''
    };

    const phoneData = {
      code: phoneCountryCode,
      iso: phoneCountryIso || countryIso2,
      number: phone || ''
    };

    const newFormData = {
      ...this.formData,
      name,
      category: categoryData,
      phone: phoneData,
      otherCategory,
      website: website ? removeProtocol(website) : '',
      facebookLink: getSocialProfile(
        SOCIAL_PROFILE_NAMES.FACEBOOK,
        facebookLink
      ),
      instagramLink: getSocialProfile(
        SOCIAL_PROFILE_NAMES.INSTAGRAM,
        instagramLink
      )
    };

    this.formData = newFormData;
    this.setFormDataClone(newFormData);

    await this.loadCategories();
    this.initialCategory = categoryData;
  }

  handleClearCategory() {
    this.formData.category = null;
  }
}
